import * as React from 'react';

import { ComboBoxRef } from '@rematter/ui-web';
import { useAtom } from 'jotai';

import { MaterialCellProps } from '../types';

import { $materialCellFocusSignal } from '~/modules/trade/_state/loadsTab.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { InventoryItemSelect } from '~/shared/components/line-items';

const FOCUS_DELAY_MS = 50;

export const MaterialCell: React.FC<MaterialCellProps> = ({
  lineItem,
  itemOptions,
  disableMaterialSelection,
  onUpdateLineItem,
  errors,
}) => {
  const t = useTranslations();
  const selectRef = React.useRef<ComboBoxRef>(null);
  const [focusSignal, setFocusSignal] = useAtom($materialCellFocusSignal);

  React.useEffect(() => {
    if (focusSignal === lineItem.id) {
      setTimeout(() => {
        selectRef.current?.focus();
      }, FOCUS_DELAY_MS);
      setFocusSignal(null);
    }
  }, [focusSignal, lineItem.id, setFocusSignal]);

  const onChangeItem = React.useCallback(
    (selectedId?: string) => {
      if (!selectedId) return;

      onUpdateLineItem(lineItem.id, { itemId: selectedId });
    },
    [lineItem.id, onUpdateLineItem],
  );

  const options = React.useMemo(
    () =>
      (itemOptions ?? []).map((item) => ({
        id: item.id,
        label: item.label,
      })),
    [itemOptions],
  );

  return (
    <InventoryItemSelect
      options={options}
      value={lineItem.itemId}
      disabled={disableMaterialSelection}
      onChange={onChangeItem}
      errorMessage={errors?.itemId}
      isInvalid={!!errors?.itemId}
      className="@MaterialCellComponent"
      placeholder={t.loadsView.form.placeholders.material}
      useLocalOptions={lineItem.pendingSalesOrderMaterialSelection}
      fieldRef={selectRef as React.RefObject<ComboBoxRef>}
    />
  );
};
